import React, {createContext, useContext, useEffect, useState} from 'react';
import {Form, FormikProvider, useFormik} from 'formik';
import {useIntl} from 'react-intl';
import {useSelector} from 'react-redux';
import {useGlobalContext} from '../../../../common/components/core/GlobalContext';
import {useGlobalMessageContext} from '../../../../common/components/core/GlobalMessageContext';
import {useMessage} from '../../../../common/components/core/useGlobalMessage';
import {invoiceOrderTotalWithVat, isEmail} from '../../../../common/utils';
import {Currency, InvoiceOrderDocument} from '../../../../common/types';
import {RootState} from '../../../../store/configureStore';
import {currencies} from '../../../../common/enums';
import {usePaymentContext} from '../PaymentContext';

type Option = {label: string, value: string};

export type PaymentCreditCardContext = {
  currencyOptions: {label: string, value: Currency}[],
  loadInvoiceOptions: (input: string) => Promise<Option[]>
  onInvoiceChanged: (input: string) => Promise<void>
  loadCustomerOptions: (input: string) => Promise<Option[]>
  onCustomerChanged: (input: string) => Promise<void>
  updateForm: () => void
  loading: boolean
}

export type PaymentCreditCardContextProviderProps = {children: React.ReactNode}

export const defaultContext: PaymentCreditCardContext = {
  currencyOptions: [],
  loadInvoiceOptions: async () => {
    return [];
  },
  onInvoiceChanged: async () => {
  },
  loadCustomerOptions: async () => {
    return [];
  },
  onCustomerChanged: async () => {
  },
  updateForm: async () => {
  },
  loading: false
};

export type PaymentFormValues = {
  invoiceID: string,
  customerName: string,
  service: string,
  email: string,
  phone: string,
  payerIsCustomer: boolean,
  payerName?: string,
  payerEmail?: string,
  payerPhone?: string,
  amount: string,
  priceIncludeVAT: boolean,
  currency: Currency,
  customerID: string,
  customerLink: string,
}

export const defaultFormValues: PaymentFormValues = {
  invoiceID: '',
  customerName: '',
  service: '',
  email: '',
  phone: '',
  payerIsCustomer: false,
  amount: '',
  priceIncludeVAT: false,
  currency: 'ils',
  customerID: '',
  customerLink: '',
};

export const PaymentCreditCardContext = createContext<PaymentCreditCardContext>(defaultContext);
PaymentCreditCardContext.displayName = 'PaymentCreditCardContext';

export const usePaymentCreditCardContext = () => useContext(PaymentCreditCardContext);

export const PaymentCreditCardContextProvider = ({children}: PaymentCreditCardContextProviderProps) => {
  const M = useMessage();
  const intl = useIntl();
  const {apiService} = useGlobalContext();
  const globalMessage = useGlobalMessageContext();
  const E = useMessage();
  const currentLocale = useSelector((state: RootState) => state.i18n.locale);
  const [loading, setLoading] = useState<boolean>(false);

  const {invoiceOrder, updateLink} = usePaymentContext();

  const formik = useFormik<PaymentFormValues>({
    initialValues: {...defaultFormValues},
    validate: (values) => {
      const errors: Partial<{ [key in keyof PaymentFormValues]: string }> = {};
      if (!values.customerName) errors.customerName = M.requiredField;
      if (!values.service) errors.service = M.requiredField;
      if (!values.email) errors.email = M.requiredField;
      if (values.email && values.email.length > 0 && !isEmail(values.email)) {
        errors.email = M.notValidEmail;
      }
      if (!values.phone) errors.phone = M.requiredField;
      if (!values.amount) errors.amount = M.requiredField;
      if (!values.payerIsCustomer && !values.payerName) errors.payerName = M.requiredField;
      if (!values.payerIsCustomer && !values.payerEmail) errors.payerEmail = M.requiredField;
      if (!values.payerIsCustomer && values.payerEmail && values.payerEmail.length > 0 && !isEmail(values.payerEmail)) {
        errors.payerEmail = M.notValidEmail;
      }
      if (!values.payerIsCustomer && !values.payerPhone) errors.payerPhone = M.requiredField;

      return errors;
    },
    onSubmit: async (values, {resetForm}) => {
      setLoading(true);
      try {
        const result = await apiService.getPaymentCreditCardPage(
          values.invoiceID || '',
          [{
            amount: parseFloat(values.amount),
            description: values.service
          }],
          values.customerID,
          values.customerName,
          values.phone,
          values.email,
          currentLocale,
          values.currency,
          values.priceIncludeVAT,
          values.payerIsCustomer,
          values.payerIsCustomer ? '' : values.payerName,
          values.payerIsCustomer ? '' : values.payerEmail,
          values.payerIsCustomer ? '' : values.payerPhone,
        );
        if (result.errorCode < 0) {
          globalMessage.open(E.serverError);
        } else {
          updateLink(result.body.url);
          resetForm();
        }
      } catch (e) {
        globalMessage.open(E.serverError);
      } finally {
        setLoading(false);
      }
    },
    enableReinitialize: true,
  });

  const update = async (invoiceOrderDTO: InvoiceOrderDocument) => {
    const invoice = {
      ...invoiceOrderDTO,
      service: invoiceOrderDTO.lines.map(line => line.description).join(', '),
      lines: invoiceOrderDTO.lines.map(line => ({
        ...line,
        amount: line.amount.toString(),
      }))
    };

    await formik.setValues({
      ...defaultFormValues,
      customerID: invoice.customerID,
      customerName: invoice.customerName,
      service: invoice.service,
      email: invoice.emailRecipients,
      phone: invoice.phone,
      amount: invoiceOrderTotalWithVat(invoice, new Date(invoice.createdAt)).toFixed(2),
      priceIncludeVAT: true
    });
  };

  const updateForm = () => {
    if (invoiceOrder) {
      update(invoiceOrder).catch(console.error);
    }
  };

  useEffect(() => {
    updateForm();
  }, [invoiceOrder]);

  const currencyOptions = [
    {
      label: intl.formatMessage({
        id: 'payment-currency-NIS',
        defaultMessage: 'NIS'
      }),
      value: currencies.NIS
    },
    {
      label: intl.formatMessage({
        id: 'payment-currency-USD',
        defaultMessage: 'USD'
      }),
      value: currencies.USD
    },
    {
      label: intl.formatMessage({
        id: 'payment-currency-EUR',
        defaultMessage: 'EUR'
      }),
      value: currencies.EUR
    },
  ];

  const loadInvoiceOptions = async (input: string) => {
    try {
      const result = await apiService.getInvoiceOptions(input);
      if (result.errorCode < 0) {
        globalMessage.open(E.serverError);
        formik.setFieldValue('invoiceID', '');

        return [];
      } else {
        if (result.body.list.length === 0) {
          formik.setFieldValue('invoiceID', '');
        }


        return result.body.list;
      }
    } catch (e) {
      globalMessage.open(E.serverError);
      formik.setFieldValue('invoiceID', '');

      return [];
    }
  };

  const onInvoiceChanged = async (invoiceID: string) => {
    try {
      const result = await apiService.getPaymentFormDetailsByInvoice(invoiceID);
      if (result.errorCode < 0) {
        globalMessage.open(E.serverError);
      } else {
        await formik.setFieldValue('customerID', result.body.customerID || '');
        await formik.setFieldValue('customerName', result.body.customerName || '');
        await formik.setFieldValue('email', result.body.email || '');
        await formik.setFieldValue('phone', result.body.phone || '');
        await formik.setFieldValue('amount', result.body.amount || 0);
        await formik.setFieldValue('priceIncludeVAT', true);
      }
    } catch (e) {
      globalMessage.open(E.serverError);
    }
  };

  const loadCustomerOptions = async (input: string) => {
    try {
      const result = await apiService.getCustomerOptions(input);
      if (result.errorCode < 0) {
        globalMessage.open(M.serverError);
        return [];
      } else {
        return result.body.list;
      }
    } catch (e) {
      globalMessage.open(M.serverError);
      return [];
    }
  };

  const onCustomerChanged = async (customerID: string) => {
    try {
      const result = await apiService.getPaymentFormDetailsByCustomerID(customerID);
      if (result.errorCode < 0) {
        globalMessage.open(E.serverError);
      } else {
        await formik.setFieldValue('customerID', result.body._id || '');
        await formik.setFieldValue('customerName', result.body.customerName || '');
        await formik.setFieldValue('email', result.body.email || '');
        await formik.setFieldValue('phone', result.body.phone || '');
      }
    } catch (e) {
      globalMessage.open(E.serverError);
    }
  };

  const context: PaymentCreditCardContext = {
    ...defaultContext,
    currencyOptions,
    loadInvoiceOptions,
    onInvoiceChanged,
    loadCustomerOptions,
    onCustomerChanged,
    updateForm,
    loading
  };

  return (
    <PaymentCreditCardContext.Provider value={context}>
      <FormikProvider value={formik}>
        <Form>
          {children}
        </Form>
      </FormikProvider>
    </PaymentCreditCardContext.Provider>
  );
};
